import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment as env } from 'src/environments/environment';
import { EmailEvent } from '../../models/email-event';
import { MaterialMaster } from '../../models/material-master';
import { PlantMaster } from '../../models/plant-master';
import { Subscription } from '../../models/subscription';
import { SupplierProfile } from '../../models/supplier-profile';
import { VendorMaster } from '../../models/vendor-master';
import { StoreService } from '../store-service/store.service';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  constructor(private storeService: StoreService, private http: HttpClient) {}

  async getSwagger(): Promise<string> {
    let supplierProfiles: SupplierProfile[];

    let url = `${env.base_api_url}/api/swagger`;
    let response = await firstValueFrom(this.http.get<string>(url));

    return response!;
  }

  async initSupplierProfiles() {
    if (this.storeService.supplierProfiles.length == 0) {
      // Get supplier profiles
      let supplierRawData = await this.getAllSuppliers();
      this.storeService.initSupplierProfiles(supplierRawData);

      // Build Supplier List
      let supplierList: SupplierProfile[] = [];
      supplierRawData.forEach((value) => {
        if (
          !supplierList.find(
            (o) => o.supplierIdentity === value.supplierIdentity
          )
        ) {
          supplierList.push(value);
        }
      });
      this.storeService.initSupplierList(supplierList);
    }
  }

  async getAllSuppliers(): Promise<SupplierProfile[]> {
    let supplierProfiles: SupplierProfile[];

    let url = `${env.base_api_url}/supplier-profile`;
    let response = await firstValueFrom(this.http
      .get<{ data: SupplierProfile[] }>(url));
    supplierProfiles = response!.data;

    return supplierProfiles;
  }

  async getSupplierProfile(supplier: string): Promise<SupplierProfile[]> {
    let supplierProfiles: SupplierProfile[];

    let url = `${env.base_api_url}/supplier-profile/${supplier}`;
    let response = await firstValueFrom(this.http
      .get<{ data: SupplierProfile[] }>(url));
    supplierProfiles = response!.data;

    return supplierProfiles;
  }

  async createSupplierProfile(
    suppliersToCreate: SupplierProfile
  ): Promise<boolean> {
    let url = `${env.base_api_url}/supplier-profile`;
    let response = await firstValueFrom(this.http
      .post<number>(url, suppliersToCreate));
    return response === 201; // CREATED
  }

  async changeSupplierProfile(
    suppliersToChange: SupplierProfile
  ): Promise<boolean> {
    let url = `${env.base_api_url}/supplier-profile`;
    let response = await firstValueFrom(this.http
      .put<number>(url, suppliersToChange));
    return response === 200; // OK
  }

  async deleteProfile(suppliersToDelete: SupplierProfile): Promise<boolean> {
    let url = `${env.base_api_url}/supplier-profile`;

    let response = await firstValueFrom(this.http
      .request('delete', url, { body: suppliersToDelete }));

    return response === 200; // OK
  }

  async searchPlant(search: string): Promise<PlantMaster[]> {
    let url = `${env.base_api_url}/plant-master/search/${search}`;
    let response = await firstValueFrom(this.http.get<PlantMaster[]>(url));
    this.storeService.initPlants(response!);
    return response!;
  }

  async searchVendor(search: string): Promise<VendorMaster[]> {
    let url = `${env.base_api_url}/vendor-master/search/${search}`;
    let response = await firstValueFrom(this.http.get<VendorMaster[]>(url));
    this.storeService.initVendors(response!);
    return response!;
  }

  async searchMaterial(search: string): Promise<MaterialMaster[]> {
    let url = `${env.base_api_url}/material-master/search/${search}`;
    let response = await firstValueFrom(this.http.get<MaterialMaster[]>(url));
    this.storeService.mergeMaterials(response!);
    return response!;
  }

  async getPlantDescription(search: string): Promise<string | undefined> {
    let url = `${env.base_api_url}/plant-master/${search}`;
    return await firstValueFrom(this.http.get<string>(url));
  }

  async getVendorDescription(search: string): Promise<string | undefined> {
    let url = `${env.base_api_url}/vendor-master/${search}`;
    return await firstValueFrom(this.http.get<string>(url));
  }

  async getMaterialDescription(search: string): Promise<string | undefined> {
    let url = `${env.base_api_url}/material-master/${search}`;
    let description = await firstValueFrom(this.http.get<string>(url));
    if (description != '') {
      let mat: MaterialMaster = {
        MARA_MATNR_MaterialNumber: search,
        MAKT_MAKTX_MaterialDescription: description!,
        FullName: search + ' - ' + description,
      };
      this.storeService.addMaterials(mat);
    }
    return description;
  }

  async getAllEvents(): Promise<EmailEvent[]> {
    // Check store
    if (this.storeService.events.length > 0) {
      return this.storeService.events;
    }

    // Get events
    let events: EmailEvent[];

    let url = `${env.base_api_url}/notification/event/`;
    let response = await firstValueFrom(this.http.get<{ data: EmailEvent[] }>(url));
    events = response!.data;

    // Save to store
    this.storeService.initEvents(events);

    return events;
  }

  async getSubscriptionByEmail(email: string): Promise<Subscription[]> {
    // Check store
    if (this.storeService.userSubscriptions.length > 0) {
      return this.storeService.userSubscriptions;
    }

    return this.refreshUserSubscriptions(email);
  }

  async refreshUserSubscriptions(email: string): Promise<Subscription[]> {
    let subscriptions: Subscription[];

    // Call API
    let url = `${env.base_api_url}/notification/subscription/email/${email}`;
    let response = await firstValueFrom(this.http
      .get<{ data: Subscription[] }>(url));
    subscriptions = response!.data;

    // Update Store
    this.storeService.initUserSubscriptions(subscriptions);
    this.storeService.clearSubscriptions();
    this.storeService.initSubscriptions(await this.getAllSubscriptions());
    return subscriptions;
  }

  async getAllSubscriptions(): Promise<Subscription[]> {
    const publish = await this.getSubscriptionByEvent(
      'APODemandDataPublishedToIDP'
    );
    const ingest = await this.getSubscriptionByEvent(
      'DemandDataIngestedBySupplier'
    );

    return [...publish, ...ingest];
  }

  async getSubscriptionByEvent(eventName: string): Promise<Subscription[]> {
    // Check store
    if (this.storeService.subscriptions.length > 0) {
      const matchEvent = this.storeService.subscriptions.filter(
        (x) => x.eventName === eventName
      );
      if (matchEvent.length > 0) return matchEvent;
    }

    // Get subscription
    let subscriptions: Subscription[];

    let url = `${env.base_api_url}/notification/subscription/event/${eventName}`;
    let response = await firstValueFrom(this.http
      .get<{ data: Subscription[] }>(url));
    subscriptions = response!.data;

    // Update store
    this.storeService.addSubscriptions(subscriptions);

    return subscriptions;
  }

  async updateSubscription(
    updatedSubscription: Subscription
  ): Promise<boolean> {
    let url = `${env.base_api_url}/notification/subscription/update`;
    let response = await firstValueFrom(this.http.put(url, updatedSubscription));
    this.storeService.updateUserSubscriptions(updatedSubscription);
    this.storeService.updateSubscriptions(updatedSubscription);
    return response === 200; // OK
  }

  async createSubscription(
    createdSubscription: Subscription
  ): Promise<boolean> {
    let url = `${env.base_api_url}/notification/subscription/create`;
    let response = await firstValueFrom(this.http.post(url, createdSubscription));
    this.storeService.addUserSubscriptions(createdSubscription);
    this.storeService.addSubscription(createdSubscription);
    return response === 201; // CREATED
  }
}
